import Config from "@/config/Config";
import useDate from "@/modules/useDate";
import { PokerRunEvent } from "@models/PokerRunEvent";
import { useNotificationInjection } from "@/composables/useNotification";

export default function useSharing():
{
  shareRedeemRegistration: (eventName: string, link: string) => void;
  shareEvent: (event: PokerRunEvent) => Promise<void>;
  copyEventLinkToClipboard: (event: PokerRunEvent, onCopied?: () => void) => void;
  shareEventOnFacebook: (eventId: string) => void;
  // eslint-disable-next-line indent
} {
  const { getDate, formatDate, formatTime } = useDate();
  const notify = useNotificationInjection();

  const shareRedeemRegistration = (eventName: string, link: string) => {
    const data = {
      text: `To redeem your registration for the ${eventName} event:

        1. Install the Aces Poker Run App from the App Store.
        2. Login to the app.
        3. Click on the link.

        ${link}

        Thanks`,
      title: `Claim your ${eventName} registration!`,
    };
    window.navigator.share(data);
  };

  const shareEvent = async (event: PokerRunEvent) => {
    const rootUrl = Config.LinkUrl;
    const eventDate = getDate(event.eventDate as string) as Date;
    const startTime = new Date(event.stops[0].expectedArrivalTime as string);
    // Note: eventImage is not currently used in the share data
    // but keeping the logic for potential future use
    const dateString = formatDate(eventDate);
    const timeString = formatTime(startTime);
    const stop = event.stops[0];
    const address = `${stop.address?.name}\n${stop.address?.addressLine1}\n${stop.address?.city},${stop.address?.state}`
    const eventPath = event.eventSettings.gameFormat === "Trade Show" ?
      "lite/registeredevent" :
      "acesevent";
    const data = {
      text: `${event.name}\n\n${dateString} ${timeString}\n\n${address.trim()}\n\n`,
      url: `${rootUrl}/${eventPath}/${event.id} `,
    };

    window.navigator.share(data);
  };

  const copyEventLinkToClipboard = async (event: PokerRunEvent, onCopied?: () => void) => {
    const rootUrl = Config.LinkUrl;
    const eventPath = event.eventSettings.gameFormat === "Trade Show" ?
      "lite/registeredevent" :
      "acesevent";
    const linkText = `${rootUrl}/${eventPath}/${event.id} `;

    try {
      await navigator.clipboard.writeText(linkText);

      // Show notification
      notify.success("Link copied to clipboard", `Event link for "${event.name}" has been copied.`);

      // Also call the onCopied callback if provided (for backward compatibility)
      if (onCopied) {
        onCopied();
      }
    } catch (error: any) {
      notify.error("Failed to copy link", error.message);
      console.error("Failed to copy link:", error);
    }
  };

  const shareEventOnFacebook = (eventId: string) => {
    const linkUrl = encodeURIComponent(`${Config.LinkUrl}/acesevent/${eventId}`);
    const url = `https://www.facebook.com/sharer/sharer.php?u=${linkUrl}`;
    window.open(url, '_blank');
  };

  return {
    shareRedeemRegistration,
    shareEvent,
    copyEventLinkToClipboard,
    shareEventOnFacebook
  };
}
